import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getLocaleWeekStart,
  toIntlLocale,
  type WeekStart,
} from "@core/lib/date-utils";

/**
 * Préférence utilisateur pour le premier jour de la semaine du calendrier.
 * - "auto"   : déduit de la locale (Intl) — défaut.
 * - "monday" : forcé lundi.
 * - "sunday" : forcé dimanche.
 *
 * Persistée en localStorage (anonymes inclus). La bascule ne recharge pas la
 * page : un event custom notifie les calendriers montés pour un re-render.
 */
export type WeekStartPref = "auto" | "monday" | "sunday";

const STORAGE_KEY = "doodates_week_start";
const CHANGE_EVENT = "doodates-week-start-change";

export function getWeekStartPref(): WeekStartPref {
  if (typeof window === "undefined") return "auto";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "monday" || v === "sunday" ? v : "auto";
}

export function setWeekStartPref(pref: WeekStartPref): void {
  if (typeof window === "undefined") return;
  if (pref === "auto") window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, pref);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * Retourne le premier jour de la semaine résolu (0 = dimanche, 1 = lundi)
 * selon la préférence + la locale courante, ainsi que la préférence brute
 * et son setter (pour l'UI des paramètres).
 */
export function useWeekStart(): {
  weekStart: WeekStart;
  pref: WeekStartPref;
  setPref: (p: WeekStartPref) => void;
} {
  const { i18n } = useTranslation();
  const [pref, setPrefState] = useState<WeekStartPref>(getWeekStartPref);

  useEffect(() => {
    const sync = () => setPrefState(getWeekStartPref());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const weekStart: WeekStart =
    pref === "sunday"
      ? 0
      : pref === "monday"
        ? 1
        : getLocaleWeekStart(toIntlLocale(i18n.language));

  return { weekStart, pref, setPref: setWeekStartPref };
}
